import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AssignmentDialog from "@/components/AssignmentDialog";
import GradeRosterDialog from "@/components/GradeRosterDialog";
import { TYPE_LABEL } from "@/lib/constants";
import { Plus, ClipboardList, Loader2, Sparkles, CalendarClock, PencilLine, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";

export default function Assignments() {
  const [list, setList] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [gradeFor, setGradeFor] = useState(null);
  const [delFor, setDelFor] = useState(null);

  const remove = async () => {
    try {
      await api.delete(`/assignments/${delFor.id}`);
      toast.success("Tugas dihapus");
      setDelFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus tugas");
    }
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/assignments"), api.get("/subjects")]).then(([a, s]) => {
      setList(a.data); setSubjects(s.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Tugas</h1>
          <p className="text-slate-500 mt-1">Buat tugas online, catatan, atau latihan. Masukkan nilai langsung bila tugas dikerjakan tatap muka.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-xl h-11 bg-teal-600 hover:bg-teal-700" data-testid="add-assignment-btn">
          <Plus className="w-5 h-5 mr-2" /> Buat Tugas
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-20 text-slate-400"><ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Belum ada tugas.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {list.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col" data-testid={`assignment-card-${a.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-medium">{TYPE_LABEL[a.type]}</span>
                {a.ai_enabled && <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium"><Sparkles className="w-3 h-3" /> AI</span>}
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-800 mt-3">{a.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{a.subject_name} · Kelas {a.kelas}</p>
              {a.due_date && <p className="text-xs text-orange-600 mt-2 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Tenggat: {a.due_date.slice(0, 10)}</p>}
              {a.student_count > 0 && (
                a.pending_count > 0 ? (
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 self-start" data-testid={`pending-badge-${a.id}`}>
                    <AlertCircle className="w-3.5 h-3.5" /> {a.pending_count} dari {a.student_count} murid belum mengerjakan
                  </span>
                ) : (
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 self-start" data-testid={`pending-badge-${a.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Semua murid sudah mengerjakan
                  </span>
                )
              )}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="rounded-xl flex-1" onClick={() => setGradeFor(a)} data-testid={`input-nilai-${a.id}`}>
                  <PencilLine className="w-4 h-4 mr-1.5" /> Input Nilai
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl text-rose-600 px-2.5" onClick={() => setDelFor(a)} data-testid={`delete-assignment-${a.id}`} aria-label="Hapus tugas">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AssignmentDialog open={open} onOpenChange={setOpen} subjects={subjects} onCreated={load} />
      <GradeRosterDialog open={!!gradeFor} onOpenChange={(v) => !v && setGradeFor(null)} assignment={gradeFor} />
      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus tugas "${delFor?.title}"?`}
        description="Semua pengumpulan dan nilai murid untuk tugas ini akan ikut terhapus." onConfirm={remove} testId="delete-assignment-dialog" />
    </div>
  );
}
